import { uploadFileToS3, PendingUploadMeta, ResumeState, UploadToS3Options } from './s3-multipart-upload';
import { registerActiveUpload, isDetachedSignal } from '../utils/active-uploads-registry';
import uploaderType from '../Pages/UploaderRedux/store/uploader.type';
import { logError } from '../utils/logger';
import { announceActiveUploads, announceUploadCompleted } from '../utils/upload-tab-coordinator';

const ROW_REMOVE_DELAY_MS = 2500;

/**
 * Generates a unique task id, falling back to random bytes when crypto.randomUUID
 * is unavailable (non-secure contexts such as plain-HTTP dev environments).
 * @returns A unique string id.
 */
export function generateTaskId(): string {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  const randomBytes = new Uint8Array(16);
  window.crypto.getRandomValues(randomBytes);
  return Array.from(randomBytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export interface PersistResult {
  ok: boolean;
  message?: string;
}

export interface BackgroundUploadParams {
  file: File;
  buildObjectKey: (file: File) => string;
  persistMetadata: (objectKey: string) => Promise<PersistResult>;
  onSuccess?: () => void;
  onCancelledMessage: (message: string) => void;
  onFailedMessage: (message: string) => void;
  onSuccessMessage?: (message: string) => void;
  section?: string;
  /** Makes the upload resumable: the backend records a pending-upload row at create time. */
  pendingMeta?: PendingUploadMeta;
  /**
   * Resuming an earlier upload: reuse its taskId (so the widget row and pending
   * record line up) and its known object key + part progress, skipping create.
   */
  resume?: { taskId: string; objectKey: string; state: ResumeState };
  /**
   * Module/dev-order scope for the post-completion refresh ping. The resume path carries no
   * pendingMeta, so it supplies the scope here; otherwise the ping falls back to pendingMeta.
   */
  completionScope?: { module?: string; devOrderId?: string | null };
  /** The task id to use, so the caller can abort the upload later; a new one when omitted. */
  taskId?: string;
}

/**
 * Builds the s3-multipart-upload options for a background upload, adding only the
 * optional keys (section/pendingMeta/resume) that are actually present.
 * @param params Background upload params.
 * @param taskId Task id shared with the widget and pending record.
 * @param signal Abort signal for the upload.
 * @param onProgress Progress callback.
 * @returns Options object for uploadFileToS3.
 */
function buildUploadOptions(
  params: BackgroundUploadParams,
  taskId: string,
  signal: AbortSignal,
  onProgress: (percent: number) => void,
): UploadToS3Options {
  const { section, pendingMeta, resume } = params;
  return {
    signal,
    taskId,
    onProgress,
    keepUploadOnAbort: () => isDetachedSignal(signal),
    ...(section ? { section } : {}),
    ...(pendingMeta ? { pendingMeta } : {}),
    ...(resume ? { resume: resume.state } : {}),
  };
}

/**
 * Applies the outcome of the metadata-persist step: on success advances the
 * widget to 100%, fires the success toast/refresh and schedules row removal; on
 * failure surfaces the message and removes the row. Hoisted out of the runner so
 * the runner's control flow stays flat.
 * @returns True on a successful persist, false otherwise.
 */
function applyPersistResult(
  result: PersistResult,
  ctx: {
    params: BackgroundUploadParams;
    reportProgress: (percent: number) => void;
    removeTask: () => void;
    dispatch: (action: unknown) => void;
    taskId: string;
  },
): boolean {
  const { params, reportProgress, removeTask, dispatch, taskId } = ctx;
  if (result.ok) {
    reportProgress(100);
    if (params.onSuccessMessage && result.message) params.onSuccessMessage(result.message);
    if (params.onSuccess) params.onSuccess();
    announceUploadCompleted(taskId);
    dispatch({
      type: uploaderType.UPLOAD_COMPLETED_PING,
      payload: {
        module: params.completionScope?.module ?? params.pendingMeta?.module,
        devOrderId: params.completionScope?.devOrderId ?? params.pendingMeta?.devOrderId,
      },
    });
    setTimeout(removeTask, ROW_REMOVE_DELAY_MS);
    return true;
  }
  params.onFailedMessage(result.message || '');
  removeTask();
  return false;
}

/**
 * Runs a file upload to S3 in the background, driving the shared floating
 * uploader widget (progress + cancel) and registering the upload so logout can
 * abort it. Module-specific behaviour (S3 key convention, metadata save, refresh,
 * toasts) is supplied by the caller, so every module reuses the same choreography.
 *
 * @param dispatch - Redux dispatch.
 * @param params - Upload inputs and per-module callbacks.
 * @returns True when the file uploaded and metadata persisted successfully.
 */
export interface InstantQueueTaskParams {
  fileName: string;
  /** Drives the widget context label (e.g. 'design-supporting-doc', 'bom-supporting-doc'). */
  module?: string;
  devOrderId?: string | null;
  /** The fast metadata save (e.g. an existing-asset pick). Resolves true on success. */
  persist: () => Promise<boolean>;
  /** Called after a successful persist, e.g. to refresh the document list. */
  onSuccess?: () => void;
}

/**
 * Drives the shared floating uploader for a task that has no client byte transfer — an existing
 * Asset Library pick, whose bytes are copied server-side (S3 CopyObject). The row is pushed at 0%,
 * held while `persist` runs, then advanced to 100% and auto-removed. There is nothing to abort, so
 * no AbortController/active-upload registration is used. The 0→100 bar is cosmetic: the browser has
 * no visibility into the server-side copy, so it stays at 0% until the persist call returns.
 *
 * @param dispatch - Redux dispatch.
 * @param params - Row metadata and the persist callback.
 * @returns True when the persist succeeded.
 */
export const runInstantQueueTask =
  (dispatch: (action: unknown) => void, params: InstantQueueTaskParams) => async (): Promise<boolean> => {
    const { fileName, module, devOrderId, persist, onSuccess } = params;
    const taskId = generateTaskId();
    const removeTask = (): void => {
      dispatch({ type: uploaderType.REMOVE_TASK_FROM_LIST, payload: taskId });
    };

    dispatch({
      type: uploaderType.ADD_FILES_IN_QUEUE_UPLOADER,
      payload: {
        [taskId]: {
          taskId,
          fileName,
          percentage: 0,
          completed: false,
          cancellable: false,
          ...(module ? { module } : {}),
          ...(devOrderId ? { devOrderId } : {}),
        },
      },
    });

    try {
      const ok = await persist();
      if (ok) {
        dispatch({ type: uploaderType.UPLOADFILE_PERCENTAGE_STATUS, payload: { taskId, filePercentage: 100 } });
        if (onSuccess) onSuccess();
        dispatch({ type: uploaderType.UPLOAD_COMPLETED_PING, payload: { module, devOrderId } });
        announceUploadCompleted(taskId);
        setTimeout(removeTask, ROW_REMOVE_DELAY_MS);
        return true;
      }
      removeTask();
      return false;
    } catch (error: unknown) {
      logError('Instant queue task failed:', error);
      removeTask();
      return false;
    }
  };

export const runBackgroundUpload =
  (dispatch: (action: unknown) => void, params: BackgroundUploadParams) => async (): Promise<boolean> => {
    const { file, buildObjectKey, persistMetadata, onCancelledMessage, onFailedMessage, resume } = params;

    const taskId = resume?.taskId ?? params.taskId ?? generateTaskId();
    const controller = new AbortController();
    const unregisterUpload = registerActiveUpload(taskId, controller);
    announceActiveUploads();
    const removeTask = (): void => {
      unregisterUpload();
      dispatch({ type: uploaderType.REMOVE_TASK_FROM_LIST, payload: taskId });
    };
    const reportProgress = (percentage: number): void => {
      dispatch({ type: uploaderType.UPLOADFILE_PERCENTAGE_STATUS, payload: { taskId, filePercentage: percentage } });
    };

    dispatch({
      type: uploaderType.ADD_FILES_IN_QUEUE_UPLOADER,
      payload: {
        [taskId]: {
          ...(params.pendingMeta || {}),
          taskId,
          fileName: file.name,
          percentage: 0,
          completed: false,
          cancellable: true,
        },
      },
    });

    try {
      const objectKey = resume ? resume.objectKey : buildObjectKey(file);
      await uploadFileToS3(file, objectKey, buildUploadOptions(params, taskId, controller.signal, reportProgress));
      const result = await persistMetadata(objectKey);
      return applyPersistResult(result, { params, reportProgress, removeTask, dispatch, taskId });
    } catch (error: unknown) {
      if (controller.signal.aborted && isDetachedSignal(controller.signal)) {
        unregisterUpload();
        return false;
      }
      if (controller.signal.aborted) {
        onCancelledMessage('');
      } else {
        logError('Background upload failed:', error);
        onFailedMessage('');
      }
      removeTask();
      return false;
    }
  };
